import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, Calendar, Users, ArrowRight, Heart } from "lucide-react";
import { useLanguage, pick } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/api";
import ExperienceCard from "@/components/ExperienceCard";
import { useSEO } from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  inviata: "bg-medblue/10 text-medblue",
  attesa: "bg-terracotta/20 text-ink",
  confermata: "bg-sage/25 text-ink",
  completata: "bg-sage/40 text-ink",
  annullata: "bg-red-100 text-red-700",
};

function signInWithGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Account() {
  const { lang, t } = useLanguage();
  const { user, loading, logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useSEO(t("account_title") + " — Flegrea Experience", t("tagline"));

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/me/requests`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])).then(setRequests).catch(() => {});
    fetch(`${API}/me/favorites`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])).then(setFavorites).catch(() => {});
  }, [user]);

  if (loading) return <div className="py-40 text-center text-ink/40">{t("loading")}</div>;

  if (!user) {
    return (
      <div className="py-32 lg:py-44 text-center" data-testid="account-signin-prompt">
        <h1 className="font-serif text-4xl text-ink">{t("account_title")}</h1>
        <p className="mt-4 text-ink/60">{t("account_signin_sub")}</p>
        <button onClick={signInWithGoogle} data-testid="account-signin-button"
          className="mt-8 inline-flex items-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ink transition-colors duration-300">
          <LogIn size={15} strokeWidth={1.5} /> {t("sign_in")} — Google
        </button>
      </div>
    );
  }

  return (
    <div className="py-16 lg:py-24" data-testid="account-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-10 border-b border-stone2/50">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover border border-stone2" />
            ) : (
              <span className="w-14 h-14 rounded-full bg-medblue text-ivory flex items-center justify-center text-lg font-semibold">
                {(user.name || user.email || "?")[0].toUpperCase()}
              </span>
            )}
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-ink" data-testid="account-title">{t("account_title")}</h1>
              <p className="text-sm text-ink/50">{user.name} · {user.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="account-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/50 hover:text-ink transition-colors duration-300">
            <LogOut size={14} strokeWidth={1.5} /> {t("sign_out")}
          </button>
        </div>

        {user.is_admin && (
          <Link to="/admin" data-testid="account-admin-link"
            className="inline-flex items-center gap-2 mt-6 border border-medblue text-medblue text-[11px] font-medium uppercase tracking-[0.2em] px-5 py-3 hover:bg-medblue hover:text-ivory transition-colors duration-300">
            {t("admin_link")} <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
        )}

        {/* REQUESTS */}
        <section className="mt-14" data-testid="my-requests-section">
          <h2 className="font-serif text-2xl md:text-3xl text-ink mb-8">{t("my_requests")}</h2>
          {requests.length === 0 ? (
            <div className="border border-dashed border-stone2 p-10 text-center" data-testid="no-requests">
              <p className="text-ink/50">{t("no_requests")}</p>
              <Link to="/experiences" className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-medblue">
                {t("discover_experiences")} <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((r) => (
                <div key={r.id} className="border border-stone2/60 p-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid={`request-${r.id}`}>
                  <div className="min-w-[200px]">
                    <p className="font-serif text-xl text-ink">
                      {r.type === "custom" ? t("custom_request") : pick(r.experience_title, lang)}
                    </p>
                    <p className="text-xs text-ink/40 mt-1">{t("requested_on")}: {new Date(r.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</p>
                  </div>
                  {r.type === "booking" && (
                    <>
                      <span className="flex items-center gap-2 text-sm text-ink/60"><Calendar size={14} strokeWidth={1.5} className="text-medblue" /> {r.date || "—"} {r.time_slot ? `· ${r.time_slot}` : ""}</span>
                      <span className="flex items-center gap-2 text-sm text-ink/60"><Users size={14} strokeWidth={1.5} className="text-medblue" /> {r.participants}</span>
                      <span className="text-sm text-ink font-medium">~€{r.estimate}</span>
                    </>
                  )}
                  <span className={`ml-auto text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1.5 ${STATUS_STYLE[r.status] || STATUS_STYLE.inviata}`} data-testid={`request-status-${r.id}`}>
                    {t(`status_${r.status}`)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* FAVORITES */}
        <section className="mt-20" data-testid="my-experiences-section">
          <h2 className="font-serif text-2xl md:text-3xl text-ink mb-8 flex items-center gap-3">
            <Heart size={22} strokeWidth={1.5} className="text-terracotta" /> {t("my_experiences")}
          </h2>
          {favorites.length === 0 ? (
            <div className="border border-dashed border-stone2 p-10 text-center" data-testid="no-favorites">
              <p className="font-serif text-2xl text-ink/60 italic">{t("no_favorites")}</p>
              <Link to="/experiences" data-testid="no-favorites-cta"
                className="mt-6 inline-flex items-center gap-2 bg-ink text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-medblue transition-colors duration-300">
                {t("discover_experiences")} <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
              {favorites.map((exp) => (
                <ExperienceCard key={exp.slug} exp={exp} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
